import type { MutableRefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { useEventEmailTemplates } from "./useEventEmailTemplates";

interface UseEntryEmailTemplateParams {
  eventId: number | null | undefined;
  enabled: boolean;
  dirtyRef: MutableRefObject<boolean>;
  onError?: (error: unknown) => void;
}

export const useEntryEmailTemplate = ({
  eventId,
  enabled,
  dirtyRef,
  onError,
}: UseEntryEmailTemplateParams) => {
  const { fetchEntryEmailTemplate } = useEventEmailTemplates();
  const [subject, setSubject] = useState<string>("");
  const [bodyText, setBodyText] = useState<string>("");
  const [bodyHtml, setBodyHtml] = useState<string | null>(null);

  /**
   * Reset all form fields to empty state.
   * Extracted as a stable callback to satisfy exhaustive-deps linter.
   */
  const resetFields = useCallback(() => {
    setSubject("");
    setBodyText("");
    setBodyHtml(null);
  }, []);

  /**
   * Reset form fields when eventId changes.
   * This ensures a clean slate when switching between different events.
   */
  useEffect(() => {
    resetFields();
  }, [resetFields]);

  useEffect(() => {
    if (!eventId || !enabled || dirtyRef.current) return;

    const controller = new AbortController();
    const fetchEntryTemplate = async () => {
      try {
        const response = await fetchEntryEmailTemplate(eventId, {
          signal: controller.signal,
        });
        if (dirtyRef.current) return;
        const nextSubject = response.email_subject ?? response.subject ?? "";
        const nextBodyText =
          response.email_body_text ?? response.body_text ?? "";
        const nextBodyHtml =
          response.email_body_html ?? response.body_html ?? null;
        setSubject(nextSubject);
        setBodyText(nextBodyText ?? "");
        setBodyHtml(nextBodyHtml);
      } catch (error) {
        onError?.(error);
        const errorName = (error as { name?: string } | null)?.name;
        if (errorName !== "CanceledError" && errorName !== "AbortError") {
          console.error("Failed to fetch entry email template:", error);
        }
      }
    };

    fetchEntryTemplate();

    return () => {
      controller.abort();
    };
  }, [dirtyRef, enabled, eventId, fetchEntryEmailTemplate, onError]);

  return {
    subject,
    bodyText,
    bodyHtml,
    reset: resetFields,
  };
};
